/**
 * Staff role definitions.
 *
 * `Role` is the full set of detailed RBAC roles permissions check against.
 * `StaffRole` is the subset that is **assignable per tenant** — everything
 * except `"Super Admin"`, which is not a tenant role (BUG-211): it lives on
 * `staff_users.is_super_admin` and is resolved independently of tenant. The
 * `staff_role` Postgres enum (see the `add_staff_user_tenant_roles` migration) carries
 * exactly the `StaffRole` values, in this order.
 */
export type Role =
  | "Super Admin"
  | "Admin"
  | "Cataloger"
  | "Accounting";

/**
 * The detailed roles a user can hold **within a tenant** (the `staff_user_tenant_roles`
 * table + `staff_role` enum). Excludes `"Super Admin"` — that is the DB-level
 * `staff_users.is_super_admin` flag, never a per-tenant assignment.
 */
export type StaffRole = Exclude<Role, "Super Admin">;

/**
 * The canonical allowlist of tenant-assignable roles, matching the `staff_role`
 * Postgres enum value set exactly (same order). Any string outside this set is
 * not a valid tenant role.
 */
export const TENANT_ASSIGNABLE_ROLES: readonly StaffRole[] = [
  "Admin",
  "Cataloger",
  "Accounting",
] as const;
// BL-1446 retired the "Clerk" role (holders were converted to Admin by the
// `sunset_clerk_staff_role` migration). The clerking PERMISSIONS
// (clerking.view / clerking.manage) survive — Admin holds them.
// BL-1468 retired "Auction Coordinator" and "Sales" (holders were converted
// to Cataloger by the `consolidate_staff_roles_to_three` migration) and
// respelled "Cataloguer" -> "Cataloger" for the American customer base.

const TENANT_ASSIGNABLE_ROLE_SET: ReadonlySet<string> = new Set(TENANT_ASSIGNABLE_ROLES);

/**
 * Narrow a free-text role array (e.g. legacy `staff_users.roles`, an invite's
 * `roles`) to the valid, deduped set of tenant-assignable `StaffRole`s.
 *
 * This is the single allowlist used by every write path (invite-accept,
 * WorkOS login-sync) and mirrors the `add_staff_user_tenant_roles` backfill's WHERE
 * clause, so on-day-one behavior and ongoing writes can't diverge on what
 * counts as a valid role. Drops the empty-string default, `"Super Admin"` (not
 * a tenant role), and any legacy drift.
 *
 * @param raw - a possibly-null array of raw role strings.
 * @returns the deduped subset that are valid tenant-assignable roles.
 */
export function toStaffRoles(raw: readonly string[] | null | undefined): StaffRole[] {
  return partitionStaffRoles(raw).assignable;
}

/**
 * Split a free-text role array into the valid, deduped tenant-assignable set
 * and the values that are NOT assignable (retired roles like "Clerk" after
 * BL-1446, "Super Admin", legacy drift, typos).
 *
 * `toStaffRoles` keeps its silent-filter shape for READ/sync paths (login-sync
 * folds legacy data); WRITE paths (invite create, tenant-roles PATCH) use the
 * `nonAssignable` half to REJECT rather than silently filter — a stale caller
 * submitting a retired role must get a validation error, never a silently
 * narrowed (or emptied) grant set.
 *
 * @param raw - a possibly-null array of raw role strings.
 * @returns `assignable` (deduped valid StaffRoles, input order) and
 *   `nonAssignable` (deduped invalid values, input order).
 */
export function partitionStaffRoles(raw: readonly string[] | null | undefined): {
  assignable: StaffRole[];
  nonAssignable: string[];
} {
  const assignable: StaffRole[] = [];
  const nonAssignable: string[] = [];
  const seen = new Set<string>();
  for (const r of raw ?? []) {
    // The legacy empty-string default is noise, not an invalid role — both
    // halves ignore it (matches toStaffRoles' documented tolerance).
    if (r === "") continue;
    if (seen.has(r)) continue;
    seen.add(r);
    if (TENANT_ASSIGNABLE_ROLE_SET.has(r)) assignable.push(r as StaffRole);
    else nonAssignable.push(r);
  }
  return { assignable, nonAssignable };
}
